import { useRef, useCallback, useState, useContext } from 'react';
import { useQuery } from '@apollo/react-hooks';
import type { ApolloError } from 'apollo-client';
import { isApolloError, NetworkStatus } from 'apollo-client';
import type { GraphQLError } from 'graphql';

import {
	SPACE_DIRECTORY_PAGINATE_EXPERIENCE,
	ExperienceTrackerContext,
	ExperienceTimeout,
} from '@confluence/experience-tracker';
import { useBooleanFeatureFlag, useSessionData } from '@confluence/session-data';
import { useIsProductAdmin } from '@confluence/current-user';

import { SpaceDirectorySpacesQuery as SpacesQuery } from './SpaceDirectoryQueries.graphql';
import type {
	SpaceDirectorySpacesQuery as QueryResult,
	SpaceDirectorySpacesQueryVariables as QueryVariables,
	SpaceDirectorySpacesQuery_spaces_pageInfo as PageInfo,
	SpaceDirectorySpacesQuery_spaces_nodes as Space,
} from './__types__/SpaceDirectorySpacesQuery';
import type { Filters } from './types';
import { PillFilter } from './Filters/PillFilters';

const PAGE_SIZE = 25;

export enum LoadingState {
	done,
	initial,
	pagination,
}

const getLoadingState = (networkStatus: NetworkStatus) => {
	if (networkStatus === NetworkStatus.setVariables) {
		return LoadingState.initial;
	}
	if (networkStatus === NetworkStatus.fetchMore || networkStatus === NetworkStatus.loading) {
		return LoadingState.pagination;
	}
	return LoadingState.done;
};

const normalizeQueryData = (data: QueryResult | undefined) => {
	let unfilteredSpaces: (Space | null | undefined)[] | null | undefined;
	let pageInfo: PageInfo = { hasNextPage: false, endCursor: null };

	if (data !== undefined && data.spaces) {
		unfilteredSpaces = data.spaces?.nodes;
		pageInfo = data.spaces?.pageInfo ?? pageInfo;
	}

	const spaces = (unfilteredSpaces ?? []).filter(Boolean) as Space[];

	return {
		spaces,
		pageInfo,
	};
};

function isWatchedError(error: GraphQLError): boolean;
function isWatchedError(error: ApolloError): boolean;
function isWatchedError(error: GraphQLError | ApolloError): boolean {
	const predicate = ({ path }: GraphQLError) =>
		!!path && path[0] === 'spaces' && path[4] === 'isWatched';
	return isApolloError(error)
		? error.graphQLErrors.every((err) => predicate(err))
		: predicate(error);
}

export const useSpaceDirectoryData = ({ filters }: { filters: Filters }) => {
	const page = useRef(0);
	const { isLicensed } = useSessionData();
	const { isProductAdmin } = useIsProductAdmin();
	const [nextPageError, setNextPageError] = useState<ApolloError>();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const isSpaceAliasFFEnabled = useBooleanFeatureFlag('confluence.frontend.space.alias');

	const getVariables = (): QueryVariables => {
		const variables: QueryVariables = {
			first: PAGE_SIZE,
			offset: 0,
			name: filters.search,
			status: 'current',
			isLicensed,
			includeAlias: isSpaceAliasFFEnabled,
		};

		switch (filters.spaceType) {
			case PillFilter.COMMUNAL:
				variables.excludeTypes = 'system,personal';
				break;
			case PillFilter.WATCHING:
				variables.watching = true;
				break;
			case PillFilter.STARRED:
				variables.favourite = true;
				break;
			case PillFilter.PERSONAL:
				variables.type = 'personal';
				break;
			case PillFilter.ARCHIVED:
				variables.status = 'archived';
				break;
			case PillFilter.TRASHED:
				if (isProductAdmin) {
					variables.status = 'trashed';
				}
				break;
		}

		if (filters.label) {
			variables.label = [filters.label];
		}

		return variables;
	};

	const {
		data,
		loading,
		error: firstPageError,
		fetchMore,
		networkStatus,
	} = useQuery<QueryResult, QueryVariables>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		SpacesQuery,
		{
			variables: getVariables(),
			notifyOnNetworkStatusChange: true,
			errorPolicy: 'all',
			fetchPolicy: 'network-only',
		},
	);

	if (firstPageError && isWatchedError(firstPageError)) {
		firstPageError.extraInfo = { isWatchedError: true };
	}

	const {
		spaces,
		pageInfo: { hasNextPage, endCursor },
	} = normalizeQueryData(data);

	const nextPage = useCallback(async () => {
		if (hasNextPage && endCursor && !loading && !firstPageError && !nextPageError) {
			experienceTracker.start({
				name: SPACE_DIRECTORY_PAGINATE_EXPERIENCE,
				timeout: ExperienceTimeout.SPACE_DIRECTORY,
			});
			page.current++;
			const { errors } = await fetchMore({
				variables: {
					offset: page.current * PAGE_SIZE,
				},
				updateQuery: (prevResult, { fetchMoreResult }) => {
					if (!fetchMoreResult) {
						return prevResult;
					}

					const { spaces: prevSpaces } = normalizeQueryData(prevResult);
					const { spaces: fetchMoreSpaces, pageInfo: fetchMorePageInfo } =
						normalizeQueryData(fetchMoreResult);

					const mergedSpaces = [...prevSpaces, ...fetchMoreSpaces];

					return (
						fetchMoreResult && {
							spaces: {
								__typename: 'PaginatedSpaceList',
								nodes: mergedSpaces,
								pageInfo: fetchMorePageInfo,
							},
						}
					);
				},
			});

			if (errors) {
				setNextPageError({
					name: 'NextPageError',
					message: 'Space directory failed to fetch more data',
					graphQLErrors: errors,
					networkError: null,
					extraInfo: {
						isWatchedError: errors.every(isWatchedError),
					},
				});
			}
		}
	}, [
		hasNextPage,
		endCursor,
		fetchMore,
		loading,
		firstPageError,
		nextPageError,
		experienceTracker,
	]);

	return {
		spaces,
		loadingState: getLoadingState(networkStatus),
		firstPageError,
		nextPageError,
		hasNextPage: !!hasNextPage,
		nextPage,
		pageIndex: page.current,
		isSpaceAliasFFEnabled,
	};
};
